import type { ReactNode } from 'react'

/**
 * Standard top-row chrome for every screen.
 *
 * Three slots, all optional:
 *  - `leading`   — left side, typically a back/close IconButton.
 *  - `center`    — middle, typically a screen title (small uppercase
 *                  text-brand-text-muted) OR a custom widget like the
 *                  segmented progress bar on the Speaking screen.
 *  - `trailing`  — right side, one or more IconButtons.
 *
 * Layout rules:
 *  - The header always reserves a min height (`min-h-[2.25rem]`) so screens
 *    line up vertically when you navigate between them.
 *  - Leading and trailing slots can hold multiple children (rendered as a
 *    flex row); pass `<Spacer />` in trailing to balance the title when
 *    there are no actions on the right.
 *
 * Why this matters: previously each screen rolled its own header — Home
 * had no header, Speaking had `× + progress + skip`, Results had `← +
 * centered title`. Three different visual heights, three different
 * layouts. This one wrapper makes them visually rhyme.
 */

interface ScreenHeaderProps {
  leading?: ReactNode
  /** Renders as an `<h1>` if a string, otherwise rendered as-is. */
  title?: string
  /** Custom centre content. Takes precedence over `title`. */
  center?: ReactNode
  trailing?: ReactNode
  /** Optional kicker line above the title (e.g. "Speaking results"). */
  kicker?: string
}

export function ScreenHeader({
  leading,
  title,
  center,
  trailing,
  kicker,
}: ScreenHeaderProps) {
  return (
    <header className="flex items-center gap-2 min-h-[2.25rem]">
      <div className="flex items-center gap-2 flex-shrink-0">{leading}</div>

      <div className="flex-1 flex items-center justify-center text-center min-w-0">
        {center ??
          (title || kicker ? (
            <ScreenTitle title={title} kicker={kicker} />
          ) : null)}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 justify-end">
        {trailing}
      </div>
    </header>
  )
}

/** Invisible 36×36 placeholder used to balance the title when there's no
 *  trailing icon button on the right. Same width as `<IconButton>`. */
export function ScreenHeaderSpacer() {
  return <span aria-hidden className="w-9 h-9 flex-shrink-0" />
}

function ScreenTitle({ title, kicker }: { title?: string; kicker?: string }) {
  return (
    <div className="flex flex-col items-center min-w-0">
      {kicker && (
        <div className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold">
          {kicker}
        </div>
      )}
      {title && (
        <h1 className="text-sm font-semibold text-brand-text leading-tight truncate max-w-full">
          {title}
        </h1>
      )}
    </div>
  )
}
